import { FC, useEffect } from "react";
import { Card } from "react-bootstrap";
import { CreateCuttlefishProjectResult, Product } from "../../models/types";
import { useApolloClient } from "@apollo/client";
import {
    GET_PRODUCTS_BY_USER_ORGANIZATION,
    GET_PROJECTS_BY_PRODUCT
} from "../../graphql/queries";
import { OpenInCuttlefishFooter } from "./OpenInCuttlefishFooter";
import { CopyButton } from "../buttons/CopyButton";
import cx from "classnames";

interface Props {
    result: CreateCuttlefishProjectResult;
    product?: Product;
    type?: string;
}

export const CreateProjectResult: FC<Props> = ({ result, product, type }) => {
    const client = useApolloClient();
    const commands = result.success ? result.message.split("|") : [];

    const unixCommands = commands.join(" && ");
    const powerShell = commands.join(" ; ");
    const cmdLine = commands.join(" & ");

    const sshUrl =
        commands.length === 3
            ? commands[0].replace("git clone --recursive", "")
            : "";

    useEffect(() => {
        if (product !== undefined) {
            client
                .query({
                    query: GET_PRODUCTS_BY_USER_ORGANIZATION,
                    fetchPolicy: "network-only"
                })
                .then(() => {
                    client.query({
                        query: GET_PROJECTS_BY_PRODUCT,
                        variables: { productId: product.id, type: type },
                        fetchPolicy: "network-only"
                    });
                });
        }
    }, []);

    return (
        <Card className="border-success">
            <Card.Header>
                <h5 className="text-inverse">
                    {result.success
                        ? "Project Created Successfully!"
                        : "Project Creation Failed"}
                </h5>
            </Card.Header>
            <Card.Body
                className={cx("text-start", {
                    "text-danger": !result.success
                })}
            >
                {result.success ? (
                    <>
                        {commands.length === 3 ? (
                            <Card className="mt-0 mb-2 border-info">
                                <Card.Header className="text-inverse">
                                    Project Info
                                </Card.Header>
                                <Card.Body>
                                    <h6 className="text-inverse">SSH url</h6>
                                    <code>{sshUrl}</code>
                                    <CopyButton value={sshUrl} />
                                    <h6 className="mt-2 text-inverse">
                                        Seepia CLI init command
                                    </h6>
                                    <code>{commands[2]}</code>
                                    <CopyButton value={commands[2]} />
                                    <div className="border-left border-info mt-2">
                                        <div className="m-1 p-2 text-info">
                                            You can also initialize the project
                                            using the ready made snippets below
                                        </div>
                                    </div>
                                </Card.Body>
                                <OpenInCuttlefishFooter
                                    product={product}
                                    result={result}
                                />
                            </Card>
                        ) : null}
                        <Card className="my-2 border-success">
                            <Card.Header className="text-inverse">
                                Unix based systems and Git Bash
                                <div className="float-end">
                                    <CopyButton value={unixCommands} />
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <code>{unixCommands}</code>
                            </Card.Body>
                        </Card>
                        <Card className="my-2 border-analytics">
                            <Card.Header className="text-inverse">
                                Windows PowerShell
                                <div className="float-end">
                                    <CopyButton value={powerShell} />
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <code>{powerShell}</code>
                            </Card.Body>
                        </Card>
                        <Card className="my-2 border-warning">
                            <Card.Header className="text-inverse">
                                Windows CMD
                                <div className="float-end">
                                    <CopyButton value={cmdLine} />
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <code>{cmdLine}</code>
                            </Card.Body>
                        </Card>
                    </>
                ) : (
                    <>
                        <p className="text-center">{result.message}</p>
                        {result.state ? (
                            <p className="text-center">{result.state}</p>
                        ) : null}
                    </>
                )}
            </Card.Body>
        </Card>
    );
};
